import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Tabs,
    Tab,
    Box,
    IconButton,
    Typography,
    Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { TestingStep, PromptVersion } from './types';
import { PromptEditor } from './PromptEditor';
import { PromptPreview } from './PromptPreview';
import { saveDraft } from './promptStorage';

import { toast } from 'react-toastify';
import { aiMatchingService } from 'services/aiMatchingService';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

interface AIMatchingSettingsPanelProps {
    open: boolean;
    onClose: () => void;
    job: {
        jobId: string;
        jobTitle: string;
    };
}

export const AIMatchingSettingsPanel: React.FC<AIMatchingSettingsPanelProps> = ({
    open,
    onClose,
    job,
}) => {
    const [activeTab, setActiveTab] = useState<TestingStep>('refine');
    const [draftPrompt, setDraftPrompt] = useState('');
    const [versions, setVersions] = useState<PromptVersion[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState<string>('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const expertId = useSelector((state: RootState) => state.auth.user.expertId);

    // Load versions on mount
    useEffect(() => {
        if (open) {
            const fetchVersions = async () => {
                try {
                    const response = await aiMatchingService.getPromptVersions({
                        expertId,
                        jobId: '692946f6ce852a00014e9cff', // TODO: Replace with job.jobId
                    });

                    if (response.apiStatus === 'SUCCESS' && response.output.versions) {
                        // Transform API response to match our PromptVersion interface
                        const transformedVersions: PromptVersion[] = response.output.versions.map((v: {
                            versionId: string;
                            version: number;
                            prompt: { system: string; userTemplate: string };
                            status: string;
                            isActive: boolean;
                            createdAt: number;
                            changelog: string;
                            experimentName?: string;
                        }) => ({
                            id: v.versionId,
                            jobId: response.output.jobId,
                            version: v.version,
                            prompt: v.prompt,
                            createdAt: new Date(v.createdAt).toISOString(),
                            createdBy: 'System', // Can be enhanced based on backend data
                            isActive: v.isActive,
                            notes: v.changelog,
                            status: v.status,
                            experimentName: v.experimentName,
                        }));

                        setVersions(transformedVersions);
                        const activeVersion = transformedVersions.find(v => v.isActive);
                        setSelectedVersionId(activeVersion?.id || transformedVersions[0]?.id || '');
                    } else {
                        toast.error('Failed to load prompt versions');
                    }
                } catch (error) {
                    console.error('Error fetching prompt versions:', error);
                    toast.error('Failed to load prompt versions');
                }
            };

            fetchVersions();
        }
    }, [open, job.jobId]);

    const handleSetAsActive = async (versionId: string) => {
        try {
            const response = await aiMatchingService.updateActivePromptVersion({
                expertId,
                jobId: '692946f6ce852a00014e9cff', // TODO: Replace with job.jobId
                versionId,
            });

            if (response.apiStatus === 'SUCCESS') {
                toast.success('Active version updated successfully');

                // Refresh versions to update the active status
                const updatedVersions = versions.map(v => ({
                    ...v,
                    isActive: v.id === versionId,
                }));
                setVersions(updatedVersions);
            } else {
                toast.error('Failed to update active version');
            }
        } catch (error) {
            console.error('Error updating active version:', error);
            toast.error('Failed to update active version');
        }
    };

    const handleRegeneratePrompt = async (experimentName: string, modificationRequest: string) => {
        if (!modificationRequest.trim()) {
            toast.error('Please provide modification instructions');
            return;
        }

        try {
            const selectedVersion = versions.find(v => v.id === selectedVersionId);
            if (!selectedVersion) {
                toast.error('No version selected');
                return;
            }

            const response = await aiMatchingService.createPromptVersion({
                expertId,
                jobId: '692946f6ce852a00014e9cff', // TODO: Replace with job.jobId
                prompt: selectedVersion.prompt,
                changelog: modificationRequest,
                status: 'DRAFT',
                experimentName: experimentName || `Experiment ${new Date().toLocaleString()}`,
                // Only send promptVersionId for drafts to update existing draft
                // For active versions, omit it to create a new draft
                ...(selectedVersion.status === 'DRAFT' && { promptVersionId: selectedVersion.id }),
            });

            if (response.apiStatus === 'SUCCESS') {
                toast.success('Prompt regenerated successfully');

                // Add the new version to the list
                const newVersion: PromptVersion = {
                    id: response.output.versionId,
                    jobId: response.output.jobId,
                    version: response.output.version,
                    prompt: response.output.prompt,
                    createdAt: new Date(response.output.createdAt).toISOString(),
                    createdBy: 'AI Generated',
                    isActive: false,
                    notes: response.output.changelog,
                    status: response.output.status,
                    experimentName: response.output.experimentName,
                };

                setVersions([newVersion, ...versions]);
                setSelectedVersionId(newVersion.id);
            } else {
                toast.error('Failed to regenerate prompt');
            }
        } catch (error) {
            console.error('Error regenerating prompt:', error);
            toast.error('Failed to regenerate prompt');
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: TestingStep) => {
        setActiveTab(newValue);
    };

    const handleVersionSelect = (versionId: string) => {
        setSelectedVersionId(versionId);
        setDraftPrompt(''); // Clear draft when switching versions
    };

    const handleDraftPromptChange = (prompt: string) => {
        setDraftPrompt(prompt);
    };

    const handleSaveDraft = () => {
        if (!draftPrompt.trim()) {
            toast.error('Prompt cannot be empty');
            return;
        }

        const draftName = `Draft ${new Date().toLocaleString()}`;
        const selectedVersion = versions.find(v => v.id === selectedVersionId);

        saveDraft(job.jobId, {
            jobId: job.jobId,
            name: draftName,
            prompt: {
                system: selectedVersion?.prompt?.system || '',
                userTemplate: draftPrompt,
            },
            baseVersionId: selectedVersionId,
        });

        toast.success('Draft saved successfully');
    };

    const displayPrompt = draftPrompt
        ? { system: '', userTemplate: draftPrompt }
        : (versions.find(v => v.id === selectedVersionId)?.prompt || { system: '', userTemplate: '' });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '85vh',
                    maxHeight: '85vh',
                },
            }}
        >
            <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">AI Matching Settings</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure and test AI prompts for {job.jobTitle}
                            {versions.find(v => v.id === selectedVersionId)?.status === 'DRAFT' && (
                                <>
                                    {' • '}
                                    <Typography component="span" color="primary.main" fontWeight={500}>
                                        Editing: {versions.find(v => v.id === selectedVersionId)?.experimentName || 'New experiment'}
                                    </Typography>
                                </>
                            )}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {versions.find(v => v.id === selectedVersionId)?.status === 'DRAFT' && handleSaveDraft && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleSaveDraft}
                                sx={{ textTransform: 'none', fontWeight: 500 }}
                            >
                                Save
                            </Button>
                        )}
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="AI Settings Tabs"
                    sx={{ px: 3 }}
                >
                    <Tab label="Refine" value="refine" />
                    <Tab label="Preview" value="preview" disabled={!displayPrompt} />
                    <Tab label="Test" value="test" disabled={!displayPrompt} />
                    <Tab label="History" value="history" />
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 3, overflow: 'auto' }}>
                {activeTab === 'refine' && (
                    <PromptEditor
                        jobId={job.jobId}
                        expertId={expertId}
                        versions={versions}
                        selectedVersionId={selectedVersionId}
                        onVersionSelect={handleVersionSelect}
                        onSetAsActive={handleSetAsActive}
                        onRegeneratePrompt={handleRegeneratePrompt}
                        isRegenerating={isRegenerating}
                        onNavigateToPreview={() => {
                            // Auto-navigate to Preview sub-tab after regeneration
                            // This will be handled by PromptEditor's internal state
                        }}
                        draftPrompt={draftPrompt}
                        onDraftPromptChange={handleDraftPromptChange}
                        onSave={handleSaveDraft}
                    />
                )}

                {activeTab === 'preview' && (
                    <PromptPreview prompt={displayPrompt} />
                )}

                {activeTab === 'test' && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Test Prompt
                        </Typography>
                        <Typography color="text.secondary">
                            Test tab content will go here
                        </Typography>
                    </Box>
                )}

                {activeTab === 'history' && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Version History
                        </Typography>
                        <Typography color="text.secondary">
                            History tab content will go here
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog >
    );
};
